import { z } from "zod";
import { AcknowledgeAlarmRequestSchema, MarkFalseAlarmRequestSchema } from "./alarms.js";
import { JustifyMachineEventRequestSchema } from "./machines.js";
import { FinishProductionRequestSchema, RegisterLossRequestSchema } from "./production.js";
import {
  BatchCharacteristicArgs,
  IsoDate as Iso,
  Uuid,
  type CommandSpec,
} from "./assistant-catalog-types.js";

// Família Operação (action · GESTOR/OPERADOR). Cada comando reusa o MESMO service da
// tela via dispatch (zero lógica nova). `production.finish`/`productionOrder.cancel` são DESTRUTIVOS
// (confirmação reforçada — digitar o nome). Args reusam os request schemas (extend) ou os espelham
// com .strict() quando o schema original tem .refine() (não é ZodObject).

export const OPERATION_COMMANDS: readonly CommandSpec[] = [
  {
    name: "alarm.acknowledge",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description: "Reconhece um alarme (com a causa).",
    argsSchema: AcknowledgeAlarmRequestSchema.extend({ alarmId: Uuid }).strict(),
  },
  {
    name: "alarm.resolve",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description: "Resolve um alarme.",
    argsSchema: z.object({ alarmId: Uuid }).strict(),
  },
  {
    name: "alarm.markFalse",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Marca um alarme como falso (com justificativa).",
    argsSchema: MarkFalseAlarmRequestSchema.extend({ alarmId: Uuid }).strict(),
  },
  {
    name: "machineEvent.acknowledge",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description: "Reconhece um evento de máquina.",
    argsSchema: z.object({ eventId: Uuid }).strict(),
  },
  {
    name: "machineEvent.justify",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description: "Justifica um evento de máquina (motivo).",
    argsSchema: JustifyMachineEventRequestSchema.extend({ eventId: Uuid }).strict(),
  },
  {
    name: "production.pause",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description: "Pausa a produção de uma linha.",
    argsSchema: z
      .object({ lineId: Uuid, runId: Uuid, observation: z.string().max(1000).optional() })
      .strict(),
  },
  {
    name: "production.resume",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description: "Retoma a produção pausada de uma linha.",
    argsSchema: z.object({ lineId: Uuid, runId: Uuid }).strict(),
  },
  {
    name: "production.finish",
    kind: "action",
    riskTier: "destructive", // encerra a produção — confirmação reforçada (nome da linha)
    minRole: "GESTOR",
    description: "Finaliza (encerra) a produção de uma linha.",
    argsSchema: FinishProductionRequestSchema.extend({ lineId: Uuid, runId: Uuid }).strict(),
  },
  {
    name: "production.recordEntry",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description: "Registra um apontamento manual de produção.",
    argsSchema: z
      .object({
        lineId: Uuid,
        // Spec 081 (FR-027) — a quantidade aceita FRAÇÃO desde que a conversão de unidade entrou.
        // `.int()` recusaria "415,5" e o agente relataria erro de validação num pedido correto.
        producedQty: z.number().nonnegative().finite().optional(),
        rejectedQty: z.number().nonnegative().finite().optional(),
        observation: z.string().max(1000).optional(),
        referenceAt: Iso.optional(),
        // Spec 081 (FR-029) — quantidade POR PRODUTO, só aceita em linha NÃO medida por máquina.
        // Sem este campo o agente teria uma versão mais pobre do mesmo comando que a tela oferece.
        products: z
          .array(z.object({ productId: Uuid, quantity: z.number().nonnegative().finite() }))
          .max(50)
          .optional(),
      })
      .strict()
      .refine(
        (v) =>
          v.producedQty != null ||
          v.rejectedQty != null ||
          v.observation != null ||
          (v.products != null && v.products.length > 0),
        { message: "Informe ao menos uma quantidade ou observação." }
      ),
  },
  {
    name: "production.registerLoss",
    kind: "action",
    riskTier: "confirm",
    minRole: "OPERADOR",
    description: "Registra refugo/perda (por máquina, opcionalmente).",
    argsSchema: RegisterLossRequestSchema.extend({ lineId: Uuid }).strict(),
  },
  {
    name: "production.updatePlannedTimes",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Ajusta os tempos planejados da run (início/fim).",
    argsSchema: z
      .object({
        lineId: Uuid,
        runId: Uuid,
        plannedStartAt: Iso.nullable().optional(),
        expectedEnd: Iso.nullable().optional(),
      })
      .strict()
      .refine((v) => v.plannedStartAt !== undefined || v.expectedEnd !== undefined, {
        message: "Informe ao menos início ou fim.",
      }),
  },
  {
    name: "productionOrder.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Cria uma ordem de produção agendada.",
    argsSchema: z
      .object({
        lineId: Uuid,
        productIds: z.array(Uuid).min(1),
        primaryProductId: Uuid,
        plannedQty: z.number().int().positive(),
        plannedStartAt: z.string().datetime({ offset: true }),
        expectedEnd: z.string().datetime({ offset: true }),
        // Prazo do cliente (opcional). SEM refine contra a janela: uma OP pode nascer
        // atrasada, e barrar isso impediria o agente de registrar um pedido real.
        dueDate: z.string().datetime({ offset: true }).nullable().optional(),
        characteristics: BatchCharacteristicArgs,
      })
      .strict()
      .refine((o) => new Date(o.expectedEnd) > new Date(o.plannedStartAt), {
        path: ["expectedEnd"],
        message: "Fim planejado deve ser após o início.",
      })
      .refine((o) => o.productIds.includes(o.primaryProductId), {
        path: ["primaryProductId"],
        message: "O produto principal deve estar entre os selecionados.",
      }),
  },
  {
    name: "productionOrder.update",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Edita uma ordem de produção agendada (campos parciais).",
    argsSchema: z
      .object({
        orderId: Uuid,
        lineId: Uuid.optional(),
        productIds: z.array(Uuid).min(1).optional(),
        primaryProductId: Uuid.optional(),
        plannedQty: z.number().int().positive().optional(),
        plannedStartAt: z.string().datetime({ offset: true }).optional(),
        expectedEnd: z.string().datetime({ offset: true }).optional(),
        // Único campo editável com a OP JÁ em execução (renegociar prazo com o
        // cliente); sozinho no payload, o service aceita em RUNNING.
        dueDate: z.string().datetime({ offset: true }).nullable().optional(),
        characteristics: BatchCharacteristicArgs,
      })
      .strict()
      .refine(
        (o) =>
          !o.plannedStartAt ||
          !o.expectedEnd ||
          new Date(o.expectedEnd) > new Date(o.plannedStartAt),
        {
          path: ["expectedEnd"],
          message: "Fim planejado deve ser após o início.",
        }
      )
      .refine(
        (o) => !o.productIds || !o.primaryProductId || o.productIds.includes(o.primaryProductId),
        {
          path: ["primaryProductId"],
          message: "O produto principal deve estar entre os selecionados.",
        }
      ),
  },
  {
    name: "productionOrder.cancel",
    kind: "action",
    riskTier: "destructive", // cancela a OP — confirmação reforçada (código da OP)
    minRole: "GESTOR",
    description: "Cancela uma ordem de produção agendada.",
    argsSchema: z.object({ orderId: Uuid, detail: z.string().max(500).optional() }).strict(),
  },
];
